import get from 'lodash.get';

import env from '#shared/config/env.js';
import json from '#shared/util/json.js';

/**
 * Look for a country object by country code
 * @param {string} countryCode
 * @returns {object}
 */
const findCountryByCode = (countryCode) => {
	const defaultValue = {
		value: countryCode,
		label: null
	};
	return env.countries.find((country) => country.value === countryCode) || defaultValue;
};

/**
 * Set a default state (Brazil focused)
 * @param {string} countryCode
 * @returns {string}
 */
const setDefaultState = (countryCode) => {
	return countryCode === 'BR' ? env.zipMap.br.states[0]?.value : '';
};

/**
 * Decode the given string
 * @note Owner information from payment source contains encoded characters (eg. \\u010Cesk\\u00E1 Republika)
 * @param {string} value
 * @returns {string}
 */
const decodeString = (value) => {
	return /\\+/.test(value) ? json.parse(`"${value}"`) : value;
};

/**
 * Modify given billing object (replace empty strings, decode strings, etc.)
 * @param {object} object Object with values
 * @returns {object} Transformed object
 */
const modifyBillingObject = (object) => {
	const objectEntries = Object.entries(object).map(([key, value]) => {
		const decodedValue = decodeString(value);
		return [key, decodedValue || ''];
	});
	return Object.fromEntries(objectEntries);
};

/**
 * Customer schema
 * @param {object} data
 * @param {object} data.cart Object with raw cart data from eStore API
 * @param {object} data.cart.billingAddress
 * @param {string} data.cart.billingAddress.firstName
 * @param {string} data.cart.billingAddress.lastName
 * @param {string} data.cart.billingAddress.city
 * @param {string} data.cart.billingAddress.country
 * @param {string} data.cart.billingAddress.state
 * @param {string} data.cart.billingAddress.zipCode
 * @param {string} data.cart.billingAddress.company
 * @param {string} data.cart.billingAddress.address1
 * @param {string} data.cart.billingAddress.address2
 * @param {string} data.cart.billingAddress.phoneNumber
 * @param {object} data.sessionStore Session store instance
 * @returns {object}
 */
export const customerSchema = ({ cart, sessionStore }) => {
	const billing = sessionStore?.store.billing ?? {};
	const form = sessionStore?.store.form ?? {};
	const country = get(cart, 'result.billingAddress.country') || form.initial?.countryCode;
	return {
		billing: modifyBillingObject({
			firstName: get(cart, 'result.billingAddress.firstName') || form.initial?.firstName,
			lastName: get(cart, 'result.billingAddress.lastName') || form.initial?.lastName,
			email: get(cart, 'result.billingAddress.email') || billing.email || form.initial?.email || '',
			city: get(cart, 'result.billingAddress.city') || form.initial?.city,
			country,
			countryName: findCountryByCode(country).label,
			state: get(cart, 'result.billingAddress.state') || form.initial?.stateCode || setDefaultState(country), // Set a default state for BR
			zip: get(cart, 'result.billingAddress.zipCode') || form.initial?.zip,
			companyName: get(cart, 'result.billingAddress.company') || form.initial?.companyName,
			taxId: billing.taxId || form.initial?.taxId || '',
			line1: get(cart, 'result.billingAddress.address1') || form.initial?.address,
			line2: get(cart, 'result.billingAddress.address2'),
			phoneNumber: get(cart, 'result.billingAddress.phoneNumber') || form.initial?.phoneNumber
		})
	};
};
